import React, { useState } from 'react';
import { useData } from '../../data/DataProvider';
import { useToast } from '../../shared/components/ToastProvider';
import { money } from '../../shared/formatters';
import EmptyState from '../../shared/components/EmptyState';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

const emptyItem = { category: '', name: '', unit: 'each', price: 0, notes: '' };

// Price catalogue (pass 7 §7A) — the tenant's own price list that grounds
// the "Draft with AI" quote drafting. Editable list, add/remove rows.
export default function PricingCatalog() {
  const { data, add, update, remove } = useData();
  const { notify } = useToast();
  const [draft, setDraft] = useState(emptyItem);
  const [editing, setEditing] = useState(null); // { id, ...fields }
  const [busy, setBusy] = useState(false);

  const grouped = data.pricingCatalog.reduce((groups, item) => {
    const key = item.category || 'Uncategorised';
    return { ...groups, [key]: [...(groups[key] || []), item] };
  }, {});

  const createItem = async (event) => {
    event.preventDefault();
    if (!draft.name.trim()) {
      notify('Item name is required', 'error');
      return;
    }
    setBusy(true);
    try {
      await add('pricingCatalog', draft);
      setDraft(emptyItem);
      notify('Catalogue item added');
    } catch (err) {
      notify(err.message || 'Failed to add item', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await update('pricingCatalog', editing.id, editing);
      setEditing(null);
      notify('Catalogue item updated');
    } catch (err) {
      notify(err.message || 'Failed to update item', 'error');
    } finally {
      setBusy(false);
    }
  };

  const deleteItem = async (item) => {
    try {
      await remove('pricingCatalog', item.id);
      notify('Catalogue item removed');
    } catch (err) {
      notify(err.message || 'Failed to remove item', 'error');
    }
  };

  return (
    <section className="panel settings-form">
      <h3>Price catalogue</h3>
      <p className="field-hint">Your own prices ground the AI quote drafting — “Draft with AI” on a new quote matches work descriptions against this list. Keep it current.</p>

      <form className="drawer-form pricing-add" onSubmit={createItem}>
        <div className="form-row">
          <label>Category<input value={draft.category} onChange={(event) => setDraft((current) => ({ ...current, category: event.target.value }))} placeholder="e.g. Boiler service" /></label>
          <label>Unit<input value={draft.unit} onChange={(event) => setDraft((current) => ({ ...current, unit: event.target.value }))} placeholder="each / hour" /></label>
        </div>
        <div className="form-row">
          <label>Item name<input value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} placeholder="e.g. Annual boiler service" required /></label>
          <label>Default price (£)<input type="number" min="0" step="0.01" value={draft.price} onChange={(event) => setDraft((current) => ({ ...current, price: Number(event.target.value) }))} /></label>
        </div>
        <button className="primary-button" disabled={busy}>{busy ? 'Saving…' : '+ Add item'}</button>
      </form>

      {!data.pricingCatalog.length ? (
        <EmptyState title="No catalogue items yet" message="Add your standard prices above — they pre-fill quote drafts." />
      ) : (
        Object.entries(grouped).map(([category, items]) => (
          <div className="pricing-group" key={category}>
            <h4>{category}</h4>
            {items.map((item) => (
              <div className="invoice-row" key={item.id}>
                {editing?.id === item.id ? (
                  <form className="pricing-edit-row" onSubmit={saveEdit}>
                    <input value={editing.name} onChange={(event) => setEditing((current) => ({ ...current, name: event.target.value }))} aria-label="Item name" required />
                    <input value={editing.category} onChange={(event) => setEditing((current) => ({ ...current, category: event.target.value }))} aria-label="Category" />
                    <input type="number" min="0" step="0.01" value={editing.price} onChange={(event) => setEditing((current) => ({ ...current, price: Number(event.target.value) }))} aria-label="Price" />
                    <button type="submit" className="secondary-button" disabled={busy}>Save</button>
                    <button type="button" className="secondary-button" onClick={() => setEditing(null)}>Cancel</button>
                  </form>
                ) : (
                  <>
                    <div>
                      <strong>{item.name}</strong>
                      <span>{item.notes || `per ${item.unit || 'item'}`}</span>
                    </div>
                    <strong>{money(item.price)}</strong>
                    <button type="button" className="icon-button" aria-label="Edit item" onClick={() => setEditing({ ...item })}><SafeIcon icon={Icons.Settings} /></button>
                    <button type="button" className="icon-button" aria-label="Remove item" onClick={() => deleteItem(item)}><SafeIcon icon={Icons.X} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        ))
      )}
    </section>
  );
}
